import { CalendarRange } from 'lucide-react';
import {
  PortalDsBadge,
  type PortalDsBadgeVariant,
} from 'src/components/portal-design-system/portal-ds-badge';
import { PortalDsEmptyState } from 'src/components/portal-design-system/portal-ds-empty-state';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import {
  formatFlightStatusLabel,
  flightStatusKind,
  toFlightIsoDate,
  toFlightMoney,
  type FlightStatusKind,
} from 'src/redux/admin/reporting/flight-row.utils';
import {
  FlightEntityLevel,
  FlightReportRow,
} from 'src/redux/admin/reporting/reporting.types';

const STATUS_VARIANTS: Record<FlightStatusKind, PortalDsBadgeVariant> = {
  active: 'green',
  paused: 'amber',
  ended: 'gray',
  error: 'red',
  unknown: 'gray',
};

interface FlightsTableProps {
  rows: FlightReportRow[];
  entityLevel: FlightEntityLevel;
  isLoading?: boolean;
}

const ENTITY_LEVEL_LABELS: Record<string, string> = {
  campaign: 'Campaign',
  package: 'Package',
  line_item: 'Line item',
};

function formatFlightDate(value?: string | number | null): string {
  const iso = toFlightIsoDate(value);
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatEndDate(value?: string | number | null): string {
  const iso = toFlightIsoDate(value);
  if (!iso) return 'Open';
  return formatFlightDate(iso);
}

function formatBudget(value?: number | string | null, currency?: string | null): string {
  const amount = toFlightMoney(value);
  if (amount == null) return '—';
  const code = currency?.trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-GB');
  }
}

function entityName(row: FlightReportRow): string {
  return (
    row.entity_name
    || row.campaign_name
    || row.line_item_name
    || row.package_name
    || (row.entity_id != null ? String(row.entity_id) : 'Untitled')
  );
}

function entityKey(row: FlightReportRow, index: number): string {
  return [
    row.source,
    row.entity_level,
    row.entity_id,
    row.budget_schedule_id,
    index,
  ].map((part) => String(part ?? '')).join(':');
}

function FlightStatusCell({
  status,
  derived,
}: {
  status?: string | null;
  derived?: boolean | null;
}) {
  const label = formatFlightStatusLabel(status);
  if (!label) {
    return (
      <span className={portalDs.tableCellMuted}>
        —
      </span>
    );
  }

  return (
    <div>
      <PortalDsBadge
        variant={STATUS_VARIANTS[flightStatusKind(status)]}
        pill
      >
        {label}
      </PortalDsBadge>
      {derived ? (
        <div className="text-[11px] text-[#9CA3AF]">
          Inferred
        </div>
      ) : null}
    </div>
  );
}

function FlightsTableHeader({ nameLabel }: { nameLabel: string }) {
  return (
    <thead className={portalDs.tableHead}>
      <tr>
        <th className={portalDs.tableHeadCell}>
          {nameLabel}
        </th>
        <th className={portalDs.tableHeadCell}>
          Status
        </th>
        <th className={portalDs.tableHeadCell}>
          Start date
        </th>
        <th className={portalDs.tableHeadCell}>
          End date
        </th>
        <th className={cn(portalDs.tableHeadCell, 'text-right')}>
          Lifetime budget
        </th>
        <th className={cn(portalDs.tableHeadCell, 'text-right')}>
          Daily budget
        </th>
      </tr>
    </thead>
  );
}

export function FlightsTable({ rows, entityLevel, isLoading = false }: FlightsTableProps) {
  const nameLabel = entityLevel === 'campaign' ? 'Campaign' : 'Line item';
  const showLevel = entityLevel === 'line';

  if (isLoading) {
    return (
      <div
        className={portalDs.tableContainer}
        aria-busy
        aria-label="Loading flights"
      >
        <table className="w-full border-collapse text-[13.5px]">
          <FlightsTableHeader nameLabel={nameLabel} />
          <tbody>
            {Array.from({ length: 6 }, (_, index) => (
              <tr
                key={index}
                className={portalDs.tableBodyRow}
              >
                <td className={portalDs.tableCell}>
                  <Skeleton className="h-4 w-48" />
                </td>
                <td className={portalDs.tableCell}>
                  <Skeleton className="h-4 w-16" />
                </td>
                <td className={portalDs.tableCell}>
                  <Skeleton className="h-4 w-24" />
                </td>
                <td className={portalDs.tableCell}>
                  <Skeleton className="h-4 w-24" />
                </td>
                <td className={cn(portalDs.tableCell, 'text-right')}>
                  <Skeleton className="ml-auto h-4 w-20" />
                </td>
                <td className={cn(portalDs.tableCell, 'text-right')}>
                  <Skeleton className="ml-auto h-4 w-20" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={portalDs.tableContainer}>
        <PortalDsEmptyState
          icon={<CalendarRange className="h-5 w-5" />}
          title="No flights active in the last 30 days"
          description="Only flights whose start and end dates overlap the last 30 days are shown. Try another platform, account, or entity level."
        />
      </div>
    );
  }

  return (
    <div className={portalDs.tableContainer}>
      <table className="w-full border-collapse text-[13.5px]">
        <FlightsTableHeader nameLabel={nameLabel} />
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={entityKey(row, index)}
              className={portalDs.tableBodyRow}
            >
              <td className={portalDs.tableCell}>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="font-semibold text-[#0D0F1A]">
                    {entityName(row)}
                  </span>
                  {showLevel || row.advertiser_name ? (
                    <span className="flex flex-wrap items-center gap-2">
                      {showLevel ? (
                        <PortalDsBadge variant="gray">
                          {ENTITY_LEVEL_LABELS[row.entity_level ?? ''] ?? row.entity_level}
                        </PortalDsBadge>
                      ) : null}
                      {row.advertiser_name ? (
                        <span className="text-[12px] text-[#8A8EA0]">
                          {row.advertiser_name}
                        </span>
                      ) : null}
                      {showLevel && row.campaign_name && row.entity_level !== 'campaign' ? (
                        <span className="text-[12px] text-[#8A8EA0]">
                          {row.campaign_name}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              </td>
              <td className={portalDs.tableCell}>
                <FlightStatusCell
                  status={row.status}
                  derived={row.status_derived}
                />
              </td>
              <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                {formatFlightDate(row.start_date)}
                {row.dates_derived ? (
                  <div className="text-[11px] text-[#9CA3AF]">
                    Inferred
                  </div>
                ) : null}
              </td>
              <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                {formatEndDate(row.end_date)}
              </td>
              <td className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}>
                {formatBudget(row.budget, row.currency)}
                {row.budget_derived ? (
                  <div className="text-[11px] text-[#9CA3AF]">
                    Rolled up
                  </div>
                ) : null}
              </td>
              <td className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}>
                {formatBudget(row.daily_budget, row.currency)}
                {row.daily_budget_derived ? (
                  <div className="text-[11px] text-[#9CA3AF]">
                    Rolled up
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
